import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { buildEnquiryText, openWhatsApp, whatsappUrl, mailtoUrl, trackWhatsApp } from "../lib/enquiryShare";
import { WhatsAppIcon } from "./WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", company: "", message: "" };

const validate = (v) => {
  const e = {};
  if (!v.name.trim()) e.name = "Please enter your name";
  if (!/^\S+@\S+\.\S+$/.test(v.email)) e.email = "Please enter a valid email";
  if (!/^[0-9+\-\s]{10,15}$/.test(v.phone)) e.phone = "Please enter a valid mobile number";
  if (v.message.trim().length < 10) e.message = "Please describe your requirement (min 10 characters)";
  return e;
};

/**
 * variant: "green" -> white underline inputs on green section (home page)
 *          "light" -> bordered inputs on white card (contact page)
 */
const ContactForm = ({ variant = "green", compact = false, source = "website" }) => {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [shareText, setShareText] = useState("");
  const [shareMeta, setShareMeta] = useState({});

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...values, source });
      const text = buildEnquiryText(values, "general");
      setShareText(text);
      setShareMeta({ name: values.name, phone: values.phone, email: values.email });
      setDone(true);
      setValues(initial);
      const opened = openWhatsApp(text, `contact-form-${source}`, { name: values.name, phone: values.phone, email: values.email });
      toast.success("Thank you! Your enquiry has been received.", {
        description: opened
          ? "WhatsApp has opened with your enquiry - just tap Send to notify our team instantly."
          : "Use the WhatsApp button below to notify our team instantly.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg?.replace(/^Value error, /, "")).join(". ")
        : typeof detail === "string" ? detail : "Something went wrong. Please try again or call us directly.";
      toast.error("Could not send your enquiry", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  const green = variant === "green";
  const inputCls = green ? "field field--green" : "field field--light";
  const errCls = green ? "text-white text-[12px] mt-1 font-semibold" : "text-[#c0392b] text-[12px] mt-1";

  if (done) {
    return (
      <div className={`text-center py-10 ${green ? "text-white" : "text-[#231f20]"}`} data-testid="contact-success">
        <CheckCircle2 size={48} className={`mx-auto mb-4 ${green ? "text-white" : "text-[#7ab929]"}`} />
        <h3 className="text-[22px] font-bold">Enquiry Received</h3>
        <p className={`mt-2 text-[15px] ${green ? "text-white/90" : "text-[#58595b]"}`}>
          Thank you for reaching out to Anjaneya Inks and Coating. Your enquiry is saved - send it to our team instantly using the buttons below.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a href={whatsappUrl(shareText)} target="_blank" rel="noreferrer" className="btn-whatsapp" onClick={() => trackWhatsApp(shareText, "contact-success-button", shareMeta)} data-testid="contact-share-whatsapp">
            <WhatsAppIcon size={18} /> Send on WhatsApp
          </a>
          <a href={mailtoUrl(shareText, "general")} className={green ? "btn-white" : "btn-outline-dark"} data-testid="contact-share-email">
            <Mail size={16} /> Send by Email
          </a>
        </div>
        <button
          type="button"
          onClick={() => setDone(false)}
          className={`mt-6 text-[13px] underline underline-offset-4 ${green ? "text-white/90 hover:text-white" : "text-[#6d6e71] hover:text-[#231f20]"}`}
          data-testid="contact-send-another"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="w-full" data-testid="contact-form">
      <div className={`grid gap-x-12 gap-y-7 ${compact ? "grid-cols-1" : "md:grid-cols-3"}`}>
        <div>
          <input name="name" value={values.name} onChange={onChange} placeholder="Name *" className={inputCls} data-testid="contact-name" />
          {errors.name && <p className={errCls}>{errors.name}</p>}
        </div>
        <div>
          <input name="email" type="email" value={values.email} onChange={onChange} placeholder="Email *" className={inputCls} data-testid="contact-email" />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
        <div>
          <input name="phone" value={values.phone} onChange={onChange} placeholder="Mobile No. *" className={inputCls} data-testid="contact-phone" />
          {errors.phone && <p className={errCls}>{errors.phone}</p>}
        </div>
        <div>
          <input name="company" value={values.company} onChange={onChange} placeholder="Company (optional)" className={inputCls} data-testid="contact-company" />
        </div>
        <div className={compact ? "" : "md:col-span-2"}>
          <textarea
            name="message"
            value={values.message}
            onChange={onChange}
            placeholder="Please tell us your requirement *"
            rows={compact ? 4 : 1}
            className={`${inputCls} resize-none`}
            data-testid="contact-message"
          />
          {errors.message && <p className={errCls}>{errors.message}</p>}
        </div>
      </div>
      <div className={`mt-9 flex ${compact ? "justify-start" : "justify-center"}`}>
        <button
          type="submit"
          disabled={loading}
          className={`${green ? "btn-white" : "btn-green"} min-w-[210px] justify-center`}
          data-testid="contact-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          {loading ? "Sending..." : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
